import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

/**
 * Clicks a BIS (back-in-stock) trigger with retry logic for race conditions
 * where the external BIS script hasn't finished binding its listeners yet.
 * Observes direct children added to document.body (where BIS apps portal their modal);
 * retries once if nothing appears. Calls `onSettled` on success or final timeout.
 * @param {HTMLElement | null | undefined} bisLink
 * @param {() => void} [onSettled] - Called when BIS modal opens or timeout elapses
 */
export function clickBisWithRetry(bisLink, onSettled) {
  if (!(bisLink instanceof HTMLElement)) {
    onSettled?.();
    return;
  }

  let settled = false;
  /** @type {ReturnType<typeof setTimeout>[]} */
  const timeouts = [];
  const settle = () => {
    if (settled) return;
    settled = true;
    observer.disconnect();
    for (const id of timeouts) clearTimeout(id);
    onSettled?.();
  };

  // Only watch direct children of body — BIS apps portal their modal there.
  // Ignore analytics/script injections (GTM, pixels, lazy-loaded CSS, etc.)
  // so we don't settle prematurely before BIS has a chance to respond.
  const IGNORED_TAGS = new Set(['SCRIPT', 'STYLE', 'LINK', 'META', 'NOSCRIPT', 'IFRAME']);
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (IGNORED_TAGS.has(/** @type {HTMLElement} */ (node).tagName)) continue;
        settle();
        return;
      }
    }
  });
  observer.observe(document.body, { childList: true });

  // Fire once immediately, retry once at 800ms if nothing settled.
  bisLink.click();
  timeouts.push(setTimeout(() => {
    if (!settled) bisLink.click();
  }, 800));

  // Final fallback — settle even if BIS never opens.
  timeouts.push(setTimeout(settle, 2000));
}

/** Max wait (ms) for a cart response before clearing a size-only item's loading state. */
const SIZE_ONLY_ADD_TIMEOUT = 8000;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));

    // Desktop bar: loading state, error display, collapse on leave
    this.addEventListener('submit', this.#handleBarSubmit);
    this.addEventListener(ThemeEvents.cartError, this.#handleBarError);


    const productCard = this.closest('product-card');
    if (productCard) {
      productCard.addEventListener('pointerleave', this.#collapseBar);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content and resets bar loading state when cart is updated.
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();

    // Reset desktop bar loading state
    if (this.#barLoadingTimeout) clearTimeout(this.#barLoadingTimeout);
    const bar = this.querySelector('.quick-add__bar');
    const submitButton = this.querySelector('.quick-add__bar-submit');
    if (bar) bar.classList.remove('quick-add__bar--loading', 'quick-add__bar--expanded');
    if (submitButton) submitButton.classList.remove('quick-add__bar-submit--loading');
  };

  /** @type {ReturnType<typeof setTimeout> | null} */
  #barLoadingTimeout = null;

  /**
   * Shows loading state on the desktop bar submit button.
   * Includes a safety timeout to reset loading if no cart event fires.
   */
  #handleBarSubmit = () => {
    const bar = this.querySelector('.quick-add__bar');
    const submitButton = this.querySelector('.quick-add__bar-submit');
    if (!bar || !submitButton) return;

    bar.classList.add('quick-add__bar--loading');
    submitButton.classList.add('quick-add__bar-submit--loading');

    // Safety: reset loading after 10s if no cart event fires
    if (this.#barLoadingTimeout) clearTimeout(this.#barLoadingTimeout);
    this.#barLoadingTimeout = setTimeout(() => {
      bar.classList.remove('quick-add__bar--loading');
      submitButton.classList.remove('quick-add__bar-submit--loading');
    }, 10000);
  };

  /**
   * Shows error message in the bar when cart add fails.
   * Displays the message in the selected-size area which has more room.
   * @param {CustomEvent} event
   */
  #handleBarError = (event) => {
    if (this.#barLoadingTimeout) clearTimeout(this.#barLoadingTimeout);

    const bar = this.querySelector('.quick-add__bar');
    const submitButton = this.querySelector('.quick-add__bar-submit');
    const selectedDisplay = this.querySelector('.quick-add__bar-selected');
    if (!bar || !submitButton || !selectedDisplay) return;

    // Stop loader
    bar.classList.remove('quick-add__bar--loading');
    submitButton.classList.remove('quick-add__bar-submit--loading');

    // Show error in the selected-size label
    const selectedLabel = this.querySelector('.quick-add__bar-selected-label');
    if (!selectedLabel) return;
    const originalText = selectedLabel.textContent;
    const errorMessage = event.detail?.data?.description || event.detail?.data?.message || 'Erreur';
    selectedLabel.textContent = errorMessage;
    bar.classList.add('quick-add__bar--error');

    // Restore after 3s
    setTimeout(() => {
      selectedLabel.textContent = originalText;
      bar.classList.remove('quick-add__bar--error');
    }, 3000);
  };

  /**
   * Collapses the size list when the mouse leaves the product card.
   */
  #collapseBar = () => {
    const bar = this.querySelector('.quick-add__bar');
    if (!bar) return;

    bar.classList.remove('quick-add__bar--expanded');
    const selectedButton = this.querySelector('.quick-add__bar-selected');
    if (selectedButton) {
      selectedButton.setAttribute('aria-expanded', 'false');
    }

    // Remove focus from bar buttons so :focus-within doesn't keep bar visible
    const focused = bar.querySelector(':focus');
    if (focused instanceof HTMLElement) {
      focused.blur();
    }
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    // Loader on the trigger while the product page is fetched, until the modal opens
    const trigger = event.target instanceof HTMLElement ? event.target : null;
    trigger?.setAttribute('data-state', 'loading');

    try {
      const currentUrl = this.productPageUrl;

      // Check if we have cached content for this URL
      let productGrid = this.#cachedContent.get(currentUrl);

      if (!productGrid) {
        // Fetch and cache the content
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      }

      if (productGrid) {
        // Use a fresh clone from the cache
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
        this.#updateVariantPicker(productGrid);
      }

      this.#openQuickAddModal();
    } finally {
      trigger?.removeAttribute('data-state');
    }
  };

  /**
   * Desktop bar: toggles the size list expand/collapse.
   */
  handleBarExpand = () => {
    const bar = this.querySelector('.quick-add__bar');
    if (!bar) return;

    const isExpanded = bar.classList.toggle('quick-add__bar--expanded');
    const selectedButton = this.querySelector('.quick-add__bar-selected');
    if (selectedButton) {
      selectedButton.setAttribute('aria-expanded', String(isExpanded));
    }
  };

  /**
   * Desktop bar: selects a size, updates variant ID, and collapses the list.
   * @param {Event} event
   */
  handleSizeSelect = (event) => {
    const button = /** @type {HTMLButtonElement | null} */ (
      /** @type {HTMLElement} */ (event.target).closest('.quick-add__bar-size')
    );
    if (!button || button.disabled) return;

    const variantId = button.dataset.variantId;
    const sizeValue = button.dataset.sizeValue;

    // Update hidden variant ID input
    const variantInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (variantInput && variantId) {
      variantInput.value = variantId;
      variantInput.disabled = false;
    }

    // Update selected display label and remove unavailable styling
    const selectedButton = this.querySelector('.quick-add__bar-selected');
    const selectedLabel = this.querySelector('.quick-add__bar-selected-label');
    if (selectedLabel && sizeValue) {
      selectedLabel.textContent = sizeValue;
    }
    if (selectedButton) {
      selectedButton.classList.remove('quick-add__bar-selected--unavailable');
    }

    // Update active state
    for (const size of this.querySelectorAll('.quick-add__bar-size')) {
      size.classList.toggle('quick-add__bar-size--active', size === button);
    }

    // Show submit button, hide BIS button (only available sizes trigger this handler)
    const submitButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('.quick-add__bar-submit:not(.quick-add__bar-bis-action)'));
    const bisAction = /** @type {HTMLButtonElement | null} */ (this.querySelector('.quick-add__bar-bis-action'));
    if (submitButton) {
      submitButton.hidden = false;
      submitButton.disabled = false;
    }
    if (bisAction) bisAction.hidden = true;

    // Collapse the size list
    const bar = this.querySelector('.quick-add__bar');
    if (bar) {
      bar.classList.remove('quick-add__bar--expanded');
      const selectedButton = this.querySelector('.quick-add__bar-selected');
      if (selectedButton) {
        selectedButton.setAttribute('aria-expanded', 'false');
      }
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const useSizeDrawer = this.#isSizeOnly || isMobileBreakpoint();

    if (useSizeDrawer) {
      this.#buildMobileSizeDrawer(productGrid);
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    document.dispatchEvent(new Event('reactivateLazyObserver'));

    if (useSizeDrawer) {
      this.#wireMobileSizeDrawer(modalContent);
    }
  }

  /**
   * Size-only mode (trigger rendered with `data-size-only`, e.g. cart drawer upsell):
   * the size drawer is used on every breakpoint and picking a size adds to cart directly.
   * @returns {boolean}
   */
  get #isSizeOnly() {
    return this.hasAttribute('data-size-only');
  }

  /**
   * Builds a size-drawer-style layout for quick add (mobile, or any breakpoint in size-only mode).
   * Clones the size-drawer-dialog items from the product page's variant picker
   * to reuse Liquid-rendered stock info (low stock, out of stock, etc.).
   * @param {Element} productGrid - The product grid element
   */
  #buildMobileSizeDrawer(productGrid) {
    const productDetails = productGrid.querySelector('.product-details');
    const productFormComponent = productGrid.querySelector('product-form-component');
    const variantPicker = productGrid.querySelector('variant-picker');
    const mediaSection = productGrid.querySelector('.product-information__media');

    const sizeDrawerContent = document.createElement('div');
    sizeDrawerContent.classList.add('quick-add-size-drawer');

    // Drag handle
    const handle = document.createElement('div');
    handle.classList.add('quick-add-size-drawer__handle');
    handle.innerHTML = '<span class="quick-add-size-drawer__handle-bar"></span>';
    sizeDrawerContent.appendChild(handle);

    // Product recap (image + title + subtitle + price)
    const recap = this.#buildMobileRecap(productDetails, mediaSection);
    if (recap) {
      sizeDrawerContent.appendChild(recap);
    }

    // Size list — clone directly from the product page's size-drawer-dialog
    if (variantPicker) {
      const existingList = variantPicker.querySelector('.size-drawer-dialog__list');

      if (existingList) {
        const sizeList = /** @type {Element} */ (existingList.cloneNode(true));
        sizeList.className = 'quick-add-size-drawer__list';

        // Rename CSS classes from size-drawer-dialog to quick-add-size-drawer
        for (const item of sizeList.querySelectorAll('[class*="size-drawer-dialog"]')) {
          item.className = item.className.replace(/size-drawer-dialog/g, 'quick-add-size-drawer');
        }

        // Size-only: no size is chosen yet, so don't highlight the product page's preselected variant
        if (this.#isSizeOnly) {
          for (const item of sizeList.querySelectorAll('.quick-add-size-drawer__item--active')) {
            item.classList.remove('quick-add-size-drawer__item--active');
          }
        }

        sizeDrawerContent.appendChild(sizeList);

        // Size-only (click-to-add) needs a size list; without one the buy button stays visible
        sizeDrawerContent.classList.toggle('quick-add-size-drawer--size-only', this.#isSizeOnly);
      }

      // Keep variant picker hidden but in the DOM for form submission
      variantPicker.style.display = 'none';
      productGrid.appendChild(variantPicker);
    }

    // Mannequin footer
    const mannequinFooter = this.#buildMannequinFooter(productDetails);
    if (mannequinFooter) {
      sizeDrawerContent.appendChild(mannequinFooter);
    }

    // Add the form component for the add-to-cart button
    if (productFormComponent) {
      productGrid.appendChild(productFormComponent);
    }

    productDetails?.remove();
    mediaSection?.remove();

    productGrid.insertBefore(sizeDrawerContent, productGrid.firstChild);
  }

  /**
   * Builds the product recap section for the mobile size drawer.
   * Extracts image, title, subtitle, and price from the product grid.
   * @param {Element | null} productDetails - The product details element
   * @param {Element | null} mediaSection - The media gallery element
   * @returns {Element | null}
   */
  #buildMobileRecap(productDetails, mediaSection) {
    if (!productDetails) return null;

    const recap = document.createElement('div');
    recap.classList.add('quick-add-size-drawer__recap');

    // Thumbnail image from the media gallery
    const firstImg = mediaSection?.querySelector('img');
    if (firstImg) {
      const imgEl = document.createElement('img');
      imgEl.src = firstImg.src || firstImg.getAttribute('lazy-src') || '';
      imgEl.alt = firstImg.alt || '';
      imgEl.classList.add('quick-add-size-drawer__recap-image');
      imgEl.loading = 'eager';
      recap.appendChild(imgEl);
    }

    // Info container
    const info = document.createElement('div');
    info.classList.add('quick-add-size-drawer__recap-info');

    const titleLink = productDetails.querySelector('.view-product-title a');
    const productUrl = titleLink?.getAttribute('href') || '';

    // Extract title and subtitle from the header group that contains the price
    const priceEl = productDetails.querySelector('product-price');
    const headerContent = priceEl?.closest('.group-block-content');
    const textBlocks = [];

    if (headerContent) {
      for (const child of headerContent.children) {
        if (child.tagName === 'PRODUCT-PRICE') break;
        if (child.classList.contains('text-block') || child.querySelector('.text-block')) {
          textBlocks.push(child.textContent.trim());
        }
      }
    }

    // Stacked: title, subtitle, then price (keeps the price clear of the close button)
    const titleEl = document.createElement('a');
    titleEl.href = productUrl;
    titleEl.classList.add('quick-add-size-drawer__recap-title');
    titleEl.textContent = textBlocks[0] || titleLink?.textContent?.trim() || '';
    info.appendChild(titleEl);

    // Subtitle (second text block = product title / material)
    if (textBlocks.length >= 2) {
      const subtitleEl = document.createElement('span');
      subtitleEl.classList.add('quick-add-size-drawer__recap-subtitle');
      subtitleEl.textContent = textBlocks[1];
      info.appendChild(subtitleEl);
    }

    if (priceEl) {
      const priceClone = /** @type {Element} */ (priceEl.cloneNode(true));
      priceClone.className = 'quick-add-size-drawer__recap-price';
      info.appendChild(priceClone);
    }

    // Size-only mode: explicit way out for shoppers who hesitate at the size step
    if (this.#isSizeOnly && productUrl) {
      const viewProductEl = document.createElement('a');
      viewProductEl.href = productUrl;
      viewProductEl.classList.add('quick-add-size-drawer__recap-link');
      viewProductEl.textContent = Theme.translations.view_product;
      info.appendChild(viewProductEl);
    }

    recap.appendChild(info);
    return recap;
  }

  /**
   * Builds the mannequin footer for the mobile size drawer.
   * Clones the mannequin block from the product details.
   * @param {Element | null} productDetails - The product details element
   * @returns {Element | null}
   */
  #buildMannequinFooter(productDetails) {
    const mannequinEl = productDetails?.querySelector('.mannequin');
    if (!mannequinEl) return null;

    const footer = document.createElement('div');
    footer.classList.add('quick-add-size-drawer__footer');

    const mannequinClone = /** @type {Element} */ (mannequinEl.cloneNode(true));
    footer.appendChild(mannequinClone);

    return footer;
  }

  /**
   * Wires up click handlers for the size drawer items (mobile, or any breakpoint in size-only mode).
   * @param {Element} modalContent - The modal content element
   */
  #wireMobileSizeDrawer(modalContent) {
    const items = modalContent.querySelectorAll('.quick-add-size-drawer__item');

    for (const item of items) {
      item.addEventListener('click', () => {
        // Unavailable item: show loader, trigger BIS with retry, close modal once BIS opens
        if (item.classList.contains('quick-add-size-drawer__item--unavailable')) {
          // Guard: programmatic bisLink.click() bubbles up here; --loading flag prevents re-entry
          if (item.classList.contains('quick-add-size-drawer__item--loading')) return;

          const bisLink = /** @type {HTMLElement | null} */ (
            item.querySelector('.quick-add-size-drawer__bis-link, .BIS_trigger')
          );
          if (!bisLink) {
            this.#closeQuickAddModal();
            return;
          }

          item.classList.add('quick-add-size-drawer__item--loading');

          clickBisWithRetry(bisLink, () => {
            item.classList.remove('quick-add-size-drawer__item--loading');
            this.#closeQuickAddModal();
          });
          return;
        }

        if (this.#isSizeOnly) {
          this.#addSizeToCart(modalContent, item);
          return;
        }

        const value = item.dataset.sizeValue;
        const radioName = item.dataset.radioName;
        const radio = modalContent.querySelector(`input[type="radio"][name="${radioName}"][value="${value}"]`);

        if (radio && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }

        for (const el of items) {
          el.classList.toggle('quick-add-size-drawer__item--active', el === item);
        }
      });
    }
  }

  /**
   * Size-only mode: submits the modal's product form with the picked size's variant,
   * without going through the variant picker re-render. The dialog closes itself on
   * cart update; on error the loading state is cleared so another size can be picked.
   * @param {Element} modalContent - The modal content element
   * @param {HTMLElement} item - The clicked size item
   */
  #addSizeToCart(modalContent, item) {
    const productForm = modalContent.querySelector('product-form-component');
    const form = productForm?.querySelector('form');
    const variantInput = form?.querySelector('input[name="id"]');
    const { variantId } = item.dataset;
    if (!productForm || !form || !(variantInput instanceof HTMLInputElement) || !variantId) return;
    if (item.classList.contains('quick-add-size-drawer__item--loading')) return;

    item.classList.add('quick-add-size-drawer__item--loading');

    // handleSubmit() drops the submit silently when a button is disabled or the quantity
    // limit is reached, so the loading state also has a timeout as a last resort.
    const settle = new AbortController();
    const clearLoading = () => {
      item.classList.remove('quick-add-size-drawer__item--loading');
      settle.abort();
    };
    productForm.addEventListener(ThemeEvents.cartError, clearLoading, { signal: settle.signal });
    productForm.addEventListener(ThemeEvents.cartUpdate, clearLoading, { signal: settle.signal });
    const timeout = setTimeout(clearLoading, SIZE_ONLY_ADD_TIMEOUT);
    settle.signal.addEventListener('abort', () => clearTimeout(timeout));

    // The buttons reflect the variant selected when the modal opened (possibly sold out,
    // or disabled while a variant re-render is pending), not the size that was clicked.
    // The clicked item is available, so enable them before submitting.
    for (const container of productForm.querySelectorAll('add-to-cart-component')) {
      /** @type {import('./product-form').AddToCartComponent} */ (container).enable();
    }

    variantInput.value = variantId;
    variantInput.disabled = false;
    form.requestSubmit();
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  /** @type {number} */
  #touchStartY = 0;
  /** @type {number} */
  #touchCurrentY = 0;
  /** @type {boolean} */
  #isSwiping = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    this.#initSwipeToDismiss();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Releases the scroll lock set by showDialog(). With the html[scroll-lock]
   * approach, scroll position is preserved naturally — no scrollTo needed.
   */
  #restoreScrollLock() {
    document.documentElement.removeAttribute('scroll-lock');
  }

  /**
   * Initializes swipe-to-dismiss and tap-to-close on the mobile bottom sheet handle.
   * - Tap: closes the modal via closeDialog()
   * - Drag down > 80px: animate out and close
   * - Drag down < 80px: snap back
   */
  #initSwipeToDismiss() {
    const dialog = this.refs?.dialog;
    if (!dialog) return;

    const signal = this.#abortController.signal;
    /** @type {boolean} */
    let hasMoved = false;

    dialog.addEventListener('touchstart', (/** @type {TouchEvent} */ e) => {
      if (!isMobileBreakpoint()) return;

      const handle = /** @type {HTMLElement} */ (e.target).closest('.quick-add-size-drawer__handle');
      if (!handle) return;

      const touch = e.touches[0];
      if (!touch) return;

      this.#touchStartY = touch.clientY;
      this.#touchCurrentY = touch.clientY;
      this.#isSwiping = true;
      hasMoved = false;
    }, { passive: true, signal });

    dialog.addEventListener('touchmove', (/** @type {TouchEvent} */ e) => {
      if (!this.#isSwiping) return;

      const touch = e.touches[0];
      if (!touch) return;

      this.#touchCurrentY = touch.clientY;
      const deltaY = this.#touchCurrentY - this.#touchStartY;

      // Only start visual drag after a small threshold to distinguish from tap
      if (!hasMoved && Math.abs(deltaY) > 5) {
        hasMoved = true;
        dialog.style.animation = 'none';
        dialog.style.overflow = 'visible';
        dialog.style.transition = 'none';
      }

      if (hasMoved) {
        dialog.style.transform = `translateY(${Math.max(0, deltaY)}px)`;
      }
    }, { passive: true, signal });

    dialog.addEventListener('touchend', () => {
      if (!this.#isSwiping) return;
      this.#isSwiping = false;

      // Tap (no movement) → close the modal
      if (!hasMoved) {
        this.closeDialog();
        return;
      }

      const deltaY = this.#touchCurrentY - this.#touchStartY;

      if (deltaY > 80) {
        // Animate out then close
        dialog.style.transition = 'transform 0.2s cubic-bezier(0.32, 0.72, 0, 1)';
        dialog.style.transform = 'translateY(100%)';

        dialog.addEventListener('transitionend', () => {
          this.#restoreScrollLock();
          dialog.style.overflow = '';
          dialog.style.transition = '';
          dialog.style.transform = '';
          dialog.style.animation = '';
          dialog.close();
          dialog.classList.remove('dialog-closing');
          this.dispatchEvent(new DialogCloseEvent());
        }, { once: true });
      } else {
        // Snap back — keep animation: none to avoid re-triggering open animation
        dialog.style.transition = 'transform 0.2s cubic-bezier(0.32, 0.72, 0, 1)';
        dialog.style.transform = 'translateY(0)';

        dialog.addEventListener('transitionend', () => {
          dialog.style.overflow = '';
          dialog.style.transition = '';
          dialog.style.transform = '';
          // Keep animation: none — dialog is already in final position
        }, { once: true });
      }
    }, { passive: true, signal });
  }

  /**
   * Closes the dialog immediately (no animation) when a product is added to cart.
   * Must be synchronous so that scroll position is restored before the cart drawer opens.
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;

    const { dialog } = this.refs;
    if (!dialog?.open) return;

    this.#restoreScrollLock();

    dialog.style.animation = 'none';
    dialog.close();
    dialog.classList.remove('dialog-closing');
    this.dispatchEvent(new DialogCloseEvent());
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
